import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, RefreshCw, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['new', 'contacted', 'quoted', 'won', 'lost'];
const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700', contacted: 'bg-amber-100 text-amber-700',
  quoted: 'bg-purple-100 text-purple-700', won: 'bg-green-100 text-green-700', lost: 'bg-stone-100 text-stone-500'
};
const inr = (n) => '₹' + Math.round(n || 0).toLocaleString('en-IN');

export const EstimatesManager = ({ adminToken, venueSlug }) => {
  const [estimates, setEstimates] = useState([]);
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [revision, setRevision] = useState({ adjustment_label: 'Custom quotation discount', adjustment_amount: 0, notes: '' });
  const [isRevising, setIsRevising] = useState(false);

  const fetchEstimates = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/pricing-estimates`, { params: { admin_token: adminToken, venue_slug: venueSlug || undefined } });
      setEstimates(res.data.estimates || []);
    } catch {
      toast.error('Failed to load estimates');
    } finally {
      setIsLoading(false);
    }
  }, [adminToken, venueSlug]);

  useEffect(() => { fetchEstimates(); }, [fetchEstimates]);

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/admin/pricing-estimates/${id}`, { params: { admin_token: adminToken } });
      setDetail(res.data);
    } catch {
      toast.error('Failed to load estimate detail');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.post(`${API_URL}/admin/pricing-estimates/${id}/status`, { status }, { params: { admin_token: adminToken } });
      setEstimates(prev => prev.map(e => e.id === id ? { ...e, status } : e));
      if (detail?.id === id) setDetail(prev => ({ ...prev, status }));
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const submitRevision = async () => {
    if (!revision.adjustment_amount) {
      toast.error('Enter an adjustment amount');
      return;
    }
    setIsRevising(true);
    try {
      const res = await axios.post(`${API_URL}/admin/pricing-estimates/${detail.id}/revise`, revision, { params: { admin_token: adminToken } });
      setDetail(prev => ({ ...prev, revisions: [...(prev.revisions || []), res.data.revision] }));
      toast.success(`Revision ${res.data.revision.n} created — revised total ${inr(res.data.revision.revised_grand_total)}`);
      setRevision({ adjustment_label: 'Custom quotation discount', adjustment_amount: 0, notes: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create revision');
    } finally {
      setIsRevising(false);
    }
  };

  return (
    <div data-testid="estimates-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-medium text-stone-900">Calculator Estimates & Leads</h3>
          <p className="text-xs text-stone-500 mt-0.5">Every customer estimate with its full calculation. Open a lead to view, revise and re-quote.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchEstimates} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {estimates.length === 0 ? (
        <p className="text-sm text-stone-400 border border-dashed border-stone-200 rounded-lg py-10 text-center">No estimates yet{venueSlug ? ' for this venue' : ''}.</p>
      ) : (
        <div className="space-y-2">
          {estimates.map(est => (
            <div key={est.id} className="bg-white border border-stone-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`estimate-row-${est.estimate_number}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="text-sm font-medium text-stone-900">{est.customer?.name} · {est.customer?.phone}</p>
                <p className="text-xs text-stone-500">{est.estimate_number} · {est.venue_name} · {new Date(est.created_at).toLocaleString()}</p>
              </div>
              <p className="text-sm font-bold text-stone-900 tabular-nums">{inr(est.revisions?.length ? est.revisions[est.revisions.length - 1].revised_grand_total : est.result?.totals?.grand_total)}</p>
              <select
                value={est.status}
                onChange={e => updateStatus(est.id, e.target.value)}
                className={`text-xs rounded-full px-2 py-1 border-0 ${STATUS_COLORS[est.status] || STATUS_COLORS.new}`}
                data-testid={`estimate-status-${est.estimate_number}`}
              >
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button variant="outline" size="sm" onClick={() => openDetail(est.id)} data-testid={`estimate-open-${est.estimate_number}`}>
                <FileText className="w-4 h-4 mr-1" /> Open
              </Button>
              <a href={`${API_URL}/pricing/estimates/${est.id}/pdf`} target="_blank" rel="noreferrer">
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Download PDF">
                  <Download className="w-4 h-4 text-stone-500" />
                </Button>
              </a>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={v => !v && setDetail(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.estimate_number} · {detail.customer?.name}</DialogTitle>
                <DialogDescription>
                  {detail.venue_name} · {detail.customer?.phone}{detail.customer?.email ? ` · ${detail.customer.email}` : ''} · valid until {detail.valid_until}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-stone-50 border border-stone-200 rounded-lg p-3">
                <div><span className="text-stone-400">Check-in</span><p className="font-medium">{detail.result?.inputs?.check_in}</p></div>
                <div><span className="text-stone-400">Check-out</span><p className="font-medium">{detail.result?.inputs?.check_out}</p></div>
                <div><span className="text-stone-400">Guests</span><p className="font-medium">{detail.result?.inputs?.guests} ({detail.result?.inputs?.overnight_guests} overnight)</p></div>
                <div><span className="text-stone-400">Event</span><p className="font-medium">{detail.result?.inputs?.event_type || '—'}</p></div>
              </div>

              <div className="border border-stone-200 rounded-lg divide-y divide-stone-100 max-h-64 overflow-y-auto" data-testid="estimate-detail-lines">
                {(detail.result?.lines || []).map((l, i) => (
                  <div key={i} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                    <span className="truncate">{l.label}{l.qty ? ` · ${l.qty} ${l.unit || ''}` : ''}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] uppercase text-stone-400">{l.status}</span>
                      <span className="font-medium tabular-nums">{l.status === 'included' ? '—' : inr(l.amount)}</span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="text-sm space-y-1">
                {(detail.result?.discounts || []).map((d, i) => <p key={i} className="flex justify-between text-green-700"><span>Discount · {d.label}</span><span>- {inr(d.amount)}</span></p>)}
                {(detail.result?.taxes || []).map((t, i) => <p key={i} className="flex justify-between text-stone-600"><span>{t.label}</span><span>{inr(t.amount)}</span></p>)}
                {(detail.result?.deposits || []).map((d, i) => <p key={i} className="flex justify-between text-stone-600"><span>{d.label}</span><span>{inr(d.amount)}</span></p>)}
                <p className="flex justify-between font-bold border-t border-stone-200 pt-1.5"><span>Estimated Total</span><span data-testid="estimate-detail-total">{inr(detail.result?.totals?.grand_total)}</span></p>
              </div>

              {(detail.revisions || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  {detail.revisions.map(r => (
                    <p key={r.n} className="flex justify-between text-xs text-amber-800">
                      <span>Rev {r.n}: {r.adjustment_label} (- {inr(r.adjustment_amount)}){r.notes ? ` · ${r.notes}` : ''}</span>
                      <span className="font-bold">{inr(r.revised_grand_total)}</span>
                    </p>
                  ))}
                </div>
              )}

              <div className="border border-stone-200 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-stone-900">Create revised quotation</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input placeholder="Adjustment label" value={revision.adjustment_label} onChange={e => setRevision(p => ({ ...p, adjustment_label: e.target.value }))} data-testid="revision-label-input" />
                  <Input type="number" placeholder="Discount amount (₹)" value={revision.adjustment_amount || ''} onChange={e => setRevision(p => ({ ...p, adjustment_amount: Number(e.target.value) }))} data-testid="revision-amount-input" />
                </div>
                <Textarea rows={2} placeholder="Internal notes (optional)" value={revision.notes} onChange={e => setRevision(p => ({ ...p, notes: e.target.value }))} data-testid="revision-notes-input" />
                <div className="flex gap-2">
                  <Button onClick={submitRevision} disabled={isRevising} className="bg-stone-900 hover:bg-stone-800" data-testid="revision-submit-btn">
                    {isRevising ? 'Saving…' : 'Save Revision'}
                  </Button>
                  <a href={`${API_URL}/pricing/estimates/${detail.id}/pdf`} target="_blank" rel="noreferrer">
                    <Button variant="outline" data-testid="estimate-detail-pdf-btn"><Download className="w-4 h-4 mr-1" /> Download Latest PDF</Button>
                  </a>
                </div>
              </div>

              {detail.customer?.requirements && (
                <p className="text-xs text-stone-500"><span className="font-medium text-stone-700">Requirements:</span> {detail.customer.requirements}</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
